import type { APIAchievements, APIUser } from "@rosette/ts/src/business/api/api_schema.js";
import { useMutation } from "@tanstack/react-query";
import { useGameAPI } from "@/components/api/GameAPIContext.js";
import { Button } from "@rosette/web_components/src/ui/Button.js";
import { FadingDiv } from "@/components/layout/FadingDiv.js";
import { AchievementType } from "@rosette/ts/src/business/achievement/AchievementType.js";
import { grantAchievement } from "@/app/(api)/api/admin/grantAchievement/grantAchievement.js";
import { revokeAchievement } from "@/app/(api)/api/admin/revokeAchievement/revokeAchievement.js";
import { grantAllAchievements } from "@/app/(api)/api/admin/grantAllAchievements/grantAllAchievements.js";
import { revokeAllAchievements } from "@/app/(api)/api/admin/revokeAllAchievements/revokeAllAchievements.js";
import { Badge } from "@rosette/ts/src/business/badge/Badge.js";
import { grantBadge } from "@/app/(api)/api/admin/grantBadge/grantBadge.js";
import { revokeBadge } from "@/app/(api)/api/admin/revokeBadge/revokeBadge.js";
import { useEffect, useState } from "react";
import { Dropdown } from "@rosette/web_components/src/form/input/Dropdown.js";
import { useToastActions } from "@rosette/web_components/src/feedback/toast/ToastContext.js";
import { addAchievementToast } from "@/components/achievements/addAchievementToast.js";
import { AssetProvider, useAssetLoader } from "@/components/assets/AssetContext.js";
import { useAchievementResources } from "@/components/achievements/useAchievementResources.js";
import { AudioSettings } from "@rosette/web_components/src/assets/AudioSettings.js";
import { NudgeType } from "@rosette/ts/src/business/nudge/NudgeType.js";
import { loadNudge, saveNudge } from "@rosette/web_utils/src/local/nudgeStorage.js";
import { NudgeData } from "@rosette/ts/src/business/nudge/NudgeData.js";
import { GreenCheckIcon } from "@rosette/web_components/src/icon/GreenCheckIcon.js";
import { FormMessage } from "@rosette/web_components/src/ui/FormMessage.js";
import { Panel } from "@rosette/web_components/src/ui/Panel.js";
import { TextArea } from "@rosette/web_components/src/ui/TextInput.js";


interface AccountAdminControlsProps {
    user: APIUser;
    achievements: APIAchievements | null;
}

function InternalAccountAdminControls({ user, achievements }: AccountAdminControlsProps) {
    const gameAPI = useGameAPI();
    const { addToast } = useToastActions();
    const assetLoader = useAssetLoader();
    const achievementResources = useAchievementResources();

    // Enable audio for achievement toast testing
    useEffect(() => {
        assetLoader.getAudioSettings().set(AudioSettings.QUIET);
    }, [assetLoader]);

    const recalcStatsMutation = useMutation({
        mutationFn: () => gameAPI.recalculateUserStats(user.publicID),
    });

    const allAchievements = AchievementType.values();
    const RANDOM_OPTION = "random";
    const [selectedAchievementID, setSelectedAchievementID] = useState(RANDOM_OPTION);

    const earnedAchievementIDs = new Set(Object.keys(achievements ?? {}));
    const unearnedAchievements = allAchievements.filter(a => !earnedAchievementIDs.has(a.getID()));
    const earnedAchievements = allAchievements.filter(a => earnedAchievementIDs.has(a.getID()));

    // Resolve the achievement to act on. When "Random" is selected, pick a random
    // achievement from the given pool (e.g. unearned ones for grant, earned for revoke).
    const resolveAchievement = (randomPool: AchievementType[]): AchievementType => {
        if (selectedAchievementID !== RANDOM_OPTION)
            return AchievementType.getByID(selectedAchievementID);

        if (randomPool.length === 0)
            throw new Error("No matching achievements to choose from");

        return randomPool[Math.floor(Math.random() * randomPool.length)];
    };

    // Nudge debug controls
    const allNudges = NudgeType.values();
    const [selectedNudgeID, setSelectedNudgeID] = useState(allNudges[0].getID());
    const [nudgeJSON, setNudgeJSON] = useState("");
    const [nudgeError, setNudgeError] = useState<string | null>(null);
    const [nudgeSaveSuccess, setNudgeSaveSuccess] = useState(false);

    // Load nudge data when selection changes
    useEffect(() => {
        const selectedNudge = NudgeType.getByID(selectedNudgeID);
        const nudgeData = loadNudge(selectedNudge);
        setNudgeJSON(JSON.stringify(nudgeData.serialize(), null, 2));
        setNudgeError(null);
        setNudgeSaveSuccess(false);
    }, [selectedNudgeID]);

    const grantAchievementMutation = useMutation({
        mutationFn: () => {
            const achievement = resolveAchievement(unearnedAchievements);
            return grantAchievement(user.publicID, achievement);
        },
        onSuccess: () => {
            window.location.reload();
        },
    });

    const revokeAchievementMutation = useMutation({
        mutationFn: () => {
            const achievement = resolveAchievement(earnedAchievements);
            return revokeAchievement(user.publicID, achievement);
        },
        onSuccess: () => {
            window.location.reload();
        },
    });

    const grantAllAchievementsMutation = useMutation({
        mutationFn: () => grantAllAchievements(user.publicID),
        onSuccess: () => {
            window.location.reload();
        },
    });

    const revokeAllAchievementsMutation = useMutation({
        mutationFn: () => revokeAllAchievements(user.publicID),
        onSuccess: () => {
            window.location.reload();
        },
    });

    const achievementError = grantAchievementMutation.error || revokeAchievementMutation.error
        || grantAllAchievementsMutation.error || revokeAllAchievementsMutation.error;

    const allBadges = Badge.values();
    const [selectedBadgeID, setSelectedBadgeID] = useState(allBadges[0].getID());

    const grantBadgeMutation = useMutation({
        mutationFn: () => grantBadge(user.publicID, Badge.getByID(selectedBadgeID)),
        onSuccess: () => {
            window.location.reload();
        },
    });

    const revokeBadgeMutation = useMutation({
        mutationFn: () => revokeBadge(user.publicID, Badge.getByID(selectedBadgeID)),
        onSuccess: () => {
            window.location.reload();
        },
    });

    const badgeError = grantBadgeMutation.error || revokeBadgeMutation.error;

    const handleSaveNudge = () => {
        try {
            setNudgeError(null);
            setNudgeSaveSuccess(false);
            const parsedJSON = JSON.parse(nudgeJSON);
            const selectedNudge = NudgeType.getByID(selectedNudgeID);

            // Create a placeholder save function since we'll save manually
            const saveFn = () => {};
            const nudgeData = NudgeData.deserialize(selectedNudge, saveFn, parsedJSON);

            saveNudge(selectedNudge, nudgeData);
            setNudgeJSON(JSON.stringify(nudgeData.serialize(), null, 2));
            setNudgeSaveSuccess(true);
        } catch (error) {
            const message = error instanceof Error ? error.message : "Invalid JSON";
            setNudgeError(message);
            setNudgeSaveSuccess(false);
        }
    };

    return (
        <FadingDiv>
            <h2>Admin Controls</h2>
            <Panel>
                <h3 className="mt-0">Recalculate Stats</h3>
                <Button
                    onClick={() => recalcStatsMutation.mutate()}>

                    {
                        recalcStatsMutation.isPending ? "Recalculating..."
                            : (recalcStatsMutation.error ? recalcStatsMutation.error.message
                                    : (recalcStatsMutation.isSuccess ? "Recalculated"
                                            : "Recalculate Stats"))
                    }
                </Button>

                <h3>Manage Achievements</h3>
                <Dropdown
                    id="achievement-selector"
                    value={selectedAchievementID}
                    setValue={setSelectedAchievementID}
                    options={[
                        {
                            value: RANDOM_OPTION,
                            label: "Random",
                        },
                        ...allAchievements.map(achievement => ({
                            value: achievement.getID(),
                            label: achievement.getDisplayName(),
                        })),
                    ]} />

                <div className="flex gap-3 mt-3 mb-3">
                    <Button
                        onClick={() => grantAchievementMutation.mutate()}>

                        {grantAchievementMutation.isPending ? "Granting..." : "Grant"}
                    </Button>

                    <Button
                        onClick={() => revokeAchievementMutation.mutate()}>

                        {revokeAchievementMutation.isPending ? "Revoking..." : "Revoke"}
                    </Button>

                    <Button
                        onClick={() => {
                            const achievement = resolveAchievement(allAchievements);
                            addAchievementToast(achievement, addToast, achievementResources);
                        }}>

                        Toast
                    </Button>
                </div>

                <div className="flex gap-3 mb-3">
                    <Button
                        onClick={() => grantAllAchievementsMutation.mutate()}>

                        {grantAllAchievementsMutation.isPending ? "Granting All..." : "Grant All"}
                    </Button>

                    <Button
                        onClick={() => revokeAllAchievementsMutation.mutate()}>

                        {revokeAllAchievementsMutation.isPending ? "Revoking All..." : "Revoke All"}
                    </Button>
                </div>

                {achievementError && (
                    <p className="text-danger">
                        {achievementError.message}
                    </p>
                )}

                <h3>Manage Badges</h3>
                <Dropdown
                    id="badge-selector"
                    value={selectedBadgeID}
                    setValue={setSelectedBadgeID}
                    options={allBadges.map(badge => ({
                        value: badge.getID(),
                        label: badge.getName(),
                    }))} />

                <div className="flex gap-3 mt-3 mb-3">
                    <Button
                        onClick={() => grantBadgeMutation.mutate()}>

                        {grantBadgeMutation.isPending ? "Granting..." : "Grant"}
                    </Button>

                    <Button
                        onClick={() => revokeBadgeMutation.mutate()}>

                        {revokeBadgeMutation.isPending ? "Removing..." : "Remove"}
                    </Button>
                </div>

                {badgeError && (
                    <p className="text-danger">
                        {badgeError.message}
                    </p>
                )}

                <h3>Nudge Debug Controls</h3>
                <p className="text-muted mb-3">
                    View and edit nudge data stored in your browser&apos;s localStorage.
                </p>

                <div className="flex w-full flex-col items-start text-left">
                    <Dropdown
                        id="nudge-type-selector"
                        value={selectedNudgeID}
                        setValue={setSelectedNudgeID}
                        options={allNudges.map(nudge => ({
                            value: nudge.getID(),
                            label: nudge.getName(),
                        }))} />

                    <TextArea
                        value={nudgeJSON}
                        onChange={(e) => {
                            setNudgeJSON(e.target.value);
                            setNudgeError(null);
                            setNudgeSaveSuccess(false);
                        }}
                        rows={12}
                        style={{ fontFamily: "monospace" }} />

                    <Button
                        onClick={handleSaveNudge}>
                        {nudgeError ? "Error" : (nudgeSaveSuccess ? "Saved" : "Save Nudge Data")}
                    </Button>

                    {nudgeSaveSuccess && (
                        <FormMessage kind="success">
                            <GreenCheckIcon />
                            Nudge data saved successfully.
                        </FormMessage>
                    )}

                    {nudgeError && (
                        <FormMessage kind="error">
                            {nudgeError}
                        </FormMessage>
                    )}
                </div>
            </Panel>
        </FadingDiv>
    );
}

export function AccountAdminControls({ user, achievements }: AccountAdminControlsProps) {
    return (
        <AssetProvider>
            <InternalAccountAdminControls user={user} achievements={achievements} />
        </AssetProvider>
    );
}
